/* eslint-disable react/jsx-filename-extension, react/jsx-props-no-spreading */
import React from 'react';
import { render } from 'react-dom';
import { trackers } from '@rmm/error-tracker';
import VideoPlayer from '../react/ovp.jsx';
import {parseVideoParams, getPlayerPosition, getVideoClassification} from './ovpUtils.js';

const IS_LOGGING_ONLY_LEAD_MEDIA = window.loggingSettings?.onlyLeadMedia;
const { SimpleErrorTracker } = trackers;
const GENERAL_PLAYER_DATA = {
  id: IS_LOGGING_ONLY_LEAD_MEDIA ? 'player-lead-media' : 'general-task',
  position: IS_LOGGING_ONLY_LEAD_MEDIA ? 'Lead Media' : 'general-task',
  renderGroup: 'Initialization within WP',
};
let playerIdCounter = 0;

/**
 * initRender
 *
 * Triggers a render for the OVP component in lazy-mode
 * Uses Suspense to give React a valid root for the component tree while the VideoPlayer
 * component is being loaded.
 *
 * @param  {Array} targets Array of target objects containing data to render the components.
 */
const initRender = (targets) => {
  // Renders the VideoPlayer component for each target
  targets.forEach((target) => {
    // Defining general tracking parameters
    playerIdCounter += 1;
    let playerId = `player-${playerIdCounter}`;
    const trackParams = {
      id: playerId,
      position: 'pre-render',
      renderGroup: 'Initialization within WP',
    };

    // Parse the data attributes from the placeholder element and updates player position
    const parseTracker = new SimpleErrorTracker(
      'Parse data attributes from placeholder element',
      100,
      trackParams,
    );
    let playerParams;
    let playerPosition;
    let videoClassifications;
    try {
      const dataset = target?.dataset;
      playerParams = parseVideoParams(dataset?.video_params);
      videoClassifications = getVideoClassification(dataset?.video_classification, playerParams?.videoClassification);
      const isPost = parseInt(dataset?.is_post, 10);
      const isLeadMedia = parseInt(dataset?.is_lead_media, 10);
      const isEmbedVideoInPost = isPost && ! isLeadMedia;
      playerPosition = getPlayerPosition(playerParams, isEmbedVideoInPost);
      parseTracker.player.position = playerPosition;
      trackParams.position = playerPosition;
      if ('Lead Media' === playerPosition) {
        parseTracker.player.id = 'player-lead-media';
        trackParams.id = 'player-lead-media';
        playerId = 'player-lead-media';
      }
      target.dataset.player_id = playerId; // eslint-disable-line no-param-reassign
      parseTracker.sendSuccess();
    } catch (error) {
      parseTracker.sendError(error, 1);
    }

    // Initializing other trackers
    const renderOvpTracker = new SimpleErrorTracker(
      'Replaces Suspense with OVP component',
      100,
      trackParams,
    );

    // Renders the component
    render(
      <VideoPlayer
        {...target.dataset}
        playerParams={playerParams}
        playerName={playerPosition}
        tracker={renderOvpTracker}
        player_id={playerId}
        video_classifications={videoClassifications}
      />,
      target,
    );
  });
};

const entryFn = () => {
  const ovpSelector = '[data-react-component="VideoPlayer"]';
  const renderInitTracker = new SimpleErrorTracker(
    'OVP renderer initialized',
    100,
    GENERAL_PLAYER_DATA,
  );
  renderInitTracker.sendSuccess();
  const nodeSelectTracker = new SimpleErrorTracker(
    'Get OVP placeholder nodes',
    100,
    GENERAL_PLAYER_DATA,
  );
  let targets;
  try {
    targets = Array
      .from(document.querySelectorAll(ovpSelector))
      .filter((tar) => {
        const target = tar;

        // Prevent re-rendering component.
        if (target.dataset.componentInitialized) {
          return false;
        }

        target.dataset.componentInitialized = true;
        return true;
      });
    nodeSelectTracker.sendSuccess();
  } catch (error) {
    nodeSelectTracker.sendError(error, 1);
  }
  initRender(targets);

  // Attach event listener to re-render OVP components when new ones are added to the DOM
  if (! window.nxsOvpRendererEntryAttached) {
    window.nxsOvpRendererEntryAttached = true;
    document.addEventListener('ReactComponentAdded', entryFn);
  }
};

export default entryFn;
